import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from 'recharts'
import { CHART_PALETTE } from '@/lib/chart-theme'
import { StatCard, fmt$, InfoTooltip } from './shared'

const RADIAN = Math.PI / 180

function renderPieLabel({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) {
  if (percent < 0.05) return null
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5
  const x = cx + radius * Math.cos(-midAngle * RADIAN)
  const y = cy + radius * Math.sin(-midAngle * RADIAN)
  return (
    <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central"
      fontSize={12} fontWeight={600}>
      {(percent * 100).toFixed(0)}%
    </text>
  )
}

function fmtFull(v: number) {
  return '$' + v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function fmtDateShort(dateStr: string) {
  const d = new Date(dateStr + 'T00:00:00')
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export function RevenueCollectedReport({ data }: { data: any }) {
  const summary = data?.summary || {}
  const chartData = data?.chartData || {}
  const tableData = data?.tableData || []
  const monthly = chartData.monthly || []
  const byMethod = chartData.byMethod || []
  const byClient = chartData.byClient || []
  const totalCollected = summary.totalCollected || 0

  return (
    <div className="rep-report">
      {/* Stats Row */}
      <div className="rep-stats-row">
        <StatCard title="Total Collected" value={fmt$(totalCollected)} color={CHART_PALETTE[0]} />
        <StatCard title="Payments" value={summary.paymentCount || 0} color={CHART_PALETTE[1]} />
        <StatCard title="Invoices Paid" value={summary.invoiceCount || 0} color={CHART_PALETTE[2]} />
        <StatCard title="Avg Payment" value={fmt$(summary.avgPayment || 0)} color={CHART_PALETTE[3]} />
        <StatCard
          title="Outstanding"
          value={fmt$(summary.totalOutstanding || 0)}
          color="#EF4444"
          sub="Across all active invoices"
        />
      </div>

      {/* Monthly Collections Bar Chart */}
      <div className="rep-charts-row">
        <div className="rep-chart-card rep-chart-full">
          <h3 className="rep-chart-title">Monthly Collections</h3>
          <InfoTooltip text="Tracks actual cash received each month — use this to reconcile against your bank statements and for cash-basis tax reporting." />
          {monthly.length > 0 ? (
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={monthly} margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#64748B' }} axisLine={false} tickLine={false} />
                <YAxis
                  tick={{ fontSize: 12, fill: '#64748B' }} axisLine={false} tickLine={false}
                  tickFormatter={(v) => fmt$(v)}
                />
                <Tooltip content={<CollectionTooltip />} />
                <Bar dataKey="collected" fill={CHART_PALETTE[0]} radius={[4, 4, 0, 0]} maxBarSize={48} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="rep-empty-chart">No payment data for the selected period</div>
          )}
        </div>
      </div>

      {/* Payment Method Donut + Top Clients */}
      <div className="rep-charts-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* By Payment Method */}
        <div className="rep-chart-card">
          <h3 className="rep-chart-title">By Payment Method</h3>
          <InfoTooltip text="Shows how clients prefer to pay — useful for deciding which payment options to prioritize and understanding processing fee exposure." />
          {byMethod.length > 0 ? (
            <>
              <div style={{ position: 'relative' }}>
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={byMethod} cx="50%" cy="50%" labelLine={false} label={renderPieLabel}
                      outerRadius={110} innerRadius={65} dataKey="collected" stroke="none">
                      {byMethod.map((_: any, i: number) => (
                        <Cell key={i} fill={CHART_PALETTE[i % CHART_PALETTE.length]} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div className="rep-donut-center">
                  <span className="rep-donut-center-value">{fmt$(totalCollected)}</span>
                  <span className="rep-donut-center-label">Total</span>
                </div>
              </div>
              <div className="rep-area-legend" style={{ marginTop: 8 }}>
                {byMethod.map((item: any, idx: number) => (
                  <span className="rep-area-legend-item" key={idx}>
                    <span className="rep-area-legend-dot" style={{ background: CHART_PALETTE[idx % CHART_PALETTE.length] }} />
                    {item.label} — {fmtFull(item.collected)}
                  </span>
                ))}
              </div>
            </>
          ) : (
            <div className="rep-empty-chart">No data</div>
          )}
        </div>

        {/* Top Clients */}
        <div className="rep-chart-card">
          <h3 className="rep-chart-title">Top Clients by Revenue Collected</h3>
          <InfoTooltip text="Your highest-paying clients ranked by actual cash received — not just signed proposals." />
          {byClient.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={byClient.slice(0, 8)} layout="vertical" margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 12, fill: '#64748B' }} axisLine={false} tickLine={false}
                  tickFormatter={(v) => fmt$(v)} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: '#64748B' }} axisLine={false}
                  tickLine={false} width={120} />
                <Tooltip content={<ClientTooltip />} />
                <Bar dataKey="collected" fill={CHART_PALETTE[2]} radius={[0, 4, 4, 0]} maxBarSize={28} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="rep-empty-chart">No data</div>
          )}
        </div>
      </div>

      {/* Payment Details Table */}
      <div className="rep-table-card">
        <h3 className="rep-chart-title">Payment Details</h3>
        <table className="rep-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Invoice</th>
              <th>Client</th>
              <th>Method</th>
              <th>Reference</th>
              <th style={{ textAlign: 'right' }}>Amount</th>
              <th>Recorded By</th>
            </tr>
          </thead>
          <tbody>
            {tableData.length > 0 ? (
              tableData.map((row: any, idx: number) => (
                <tr key={idx}>
                  <td>{fmtDateShort(row.date)}</td>
                  <td style={{ fontFamily: "'Monaco','Menlo','Courier New',monospace", fontSize: 12 }}>{row.invoice}</td>
                  <td>{row.client}</td>
                  <td>{row.method}</td>
                  <td style={{ color: '#64748B', fontSize: 13 }}>{row.reference || '—'}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmtFull(row.amount)}</td>
                  <td style={{ color: '#64748B' }}>{row.recordedBy}</td>
                </tr>
              ))
            ) : (
              <tr><td colSpan={7} style={{ textAlign: 'center', color: '#94A3B8', padding: 32 }}>No payments recorded for the selected period</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/* ── Custom Tooltips ── */

function CollectionTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null
  return (
    <div className="rep-tooltip">
      <p className="rep-tooltip-label">{label}</p>
      <div className="rep-tooltip-row">
        <span className="rep-tooltip-dot" style={{ backgroundColor: CHART_PALETTE[0] }} />
        <span className="rep-tooltip-name">Collected:</span>
        <span className="rep-tooltip-value">{fmtFull(payload[0].value)}</span>
      </div>
      {payload[0]?.payload?.count != null && (
        <div className="rep-tooltip-row">
          <span className="rep-tooltip-name">Payments:</span>
          <span className="rep-tooltip-value">{payload[0].payload.count}</span>
        </div>
      )}
    </div>
  )
}

function ClientTooltip({ active, payload }: any) {
  if (!active || !payload?.length) return null
  const item = payload[0]?.payload
  return (
    <div className="rep-tooltip">
      <p className="rep-tooltip-label">{item?.name}</p>
      {item?.company && (
        <div className="rep-tooltip-row">
          <span className="rep-tooltip-name">{item.company}</span>
        </div>
      )}
      <div className="rep-tooltip-row">
        <span className="rep-tooltip-dot" style={{ backgroundColor: CHART_PALETTE[2] }} />
        <span className="rep-tooltip-name">Collected:</span>
        <span className="rep-tooltip-value">{fmtFull(payload[0].value)}</span>
      </div>
      <div className="rep-tooltip-row">
        <span className="rep-tooltip-name">Payments:</span>
        <span className="rep-tooltip-value">{item?.count || 0}</span>
      </div>
    </div>
  )
}
